import React from 'react';
import { cn } from '@/lib/utils';
import { Lock } from 'lucide-react';
import Image from '@/components/ui/Image';
import type { Jarticard, JarticardType } from '@/lib/types';
import { JARTICARD_TYPE_LABELS } from '@/lib/types';

/**
 * Estilo visual por tipo de carta. Cada tipo tiene una paleta y un marco
 * propio (gradientes + ornamentos SVG). "prime" añade un efecto foil/holo.
 */
interface TypeStyle {
  /** Color del marco/borde */
  frame: string;
  frame2: string;
  /** Color de acento (placa de nombre, número) */
  accent: string;
  /** Tinte del fondo bajo la foto */
  glow: string;
  /** Texto sobre el marco */
  text: string;
  label: string;
}

export const JARTICARD_STYLES: Record<JarticardType, TypeStyle> = {
  titular: {
    frame: '#fbbf24', frame2: '#b45309', accent: '#78350f', glow: '#f59e0b',
    text: '#3b2400', label: JARTICARD_TYPE_LABELS.titular,
  },
  banda: {
    frame: '#cbd5e1', frame2: '#475569', accent: '#1e293b', glow: '#64748b',
    text: '#0f172a', label: JARTICARD_TYPE_LABELS.banda,
  },
  recuerdos: {
    frame: '#d6b88a', frame2: '#8a6a3f', accent: '#5b4326', glow: '#a98c5f',
    text: '#3a2a14', label: JARTICARD_TYPE_LABELS.recuerdos,
  },
  momentos: {
    frame: '#6ee7b7', frame2: '#047857', accent: '#064e3b', glow: '#10b981',
    text: '#022c22', label: JARTICARD_TYPE_LABELS.momentos,
  },
  estandarte: {
    frame: '#f0abfc', frame2: '#86198f', accent: '#581c45', glow: '#c026d3',
    text: '#3b0a36', label: JARTICARD_TYPE_LABELS.estandarte,
  },
  iglesia: {
    frame: '#e7d8b0', frame2: '#9c8348', accent: '#5c4a1f', glow: '#cbb06a',
    text: '#352a10', label: JARTICARD_TYPE_LABELS.iglesia,
  },
  paso_cristo: {
    frame: '#fca5a5', frame2: '#991b1b', accent: '#5c0d0d', glow: '#dc2626',
    text: '#3a0606', label: JARTICARD_TYPE_LABELS.paso_cristo,
  },
  paso_virgen: {
    frame: '#bae6fd', frame2: '#2563eb', accent: '#1e3a8a', glow: '#3b82f6',
    text: '#0b1e4a', label: JARTICARD_TYPE_LABELS.paso_virgen,
  },
  escudos: {
    frame: '#f3d27a', frame2: '#a16207', accent: '#7f1d1d', glow: '#eab308',
    text: '#3a1a0a', label: JARTICARD_TYPE_LABELS.escudos,
  },
  prime: {
    frame: '#fde68a', frame2: '#a855f7', accent: '#1e1b4b', glow: '#e879f9',
    text: '#1a1330', label: JARTICARD_TYPE_LABELS.prime,
  },
  superprime: {
    frame: '#e0f2fe', frame2: '#67e8f9', accent: '#155e75', glow: '#a5f3fc',
    text: '#06303b', label: JARTICARD_TYPE_LABELS.superprime,
  },
};

const SP_SHARD_COLORS = ['#f0abfc', '#67e8f9', '#fde047', '#86efac', '#fda4af', '#a5b4fc'];

/** Silueta de gema/cristal de la Súper Prime (recortada con clip-path). */
const SUPER_CLIP =
  'polygon(50% 0%, 73% 5%, 100% 22%, 100% 78%, 73% 95%, 50% 100%, 27% 95%, 0% 78%, 0% 22%, 27% 5%)';

/** Picos de cristal BREVES sobre los vértices de la gema (solo Súper Prime). */
function CrystalSpikes() {
  // posición (en %), rotación (apuntando hacia fuera), tamaño relativo (corto)
  const spikes = [
    { left: 50, top: 0, rot: 0, w: 10, h: 11 },        // punta superior
    { left: 50, top: 100, rot: 180, w: 10, h: 11 },    // punta inferior
    { left: 0.5, top: 50, rot: -90, w: 8, h: 9 },      // lateral izq
    { left: 99.5, top: 50, rot: 90, w: 8, h: 9 },      // lateral der
    { left: 73, top: 5, rot: 42, w: 7, h: 8 },         // hombro sup der
    { left: 27, top: 5, rot: -42, w: 7, h: 8 },        // hombro sup izq
    { left: 73, top: 95, rot: 138, w: 7, h: 8 },       // hombro inf der
    { left: 27, top: 95, rot: -138, w: 7, h: 8 },      // hombro inf izq
  ];
  return (
    <div className="pointer-events-none absolute inset-0 z-40">
      {spikes.map((s, i) => {
        const c1 = SP_SHARD_COLORS[i % SP_SHARD_COLORS.length];
        const c2 = SP_SHARD_COLORS[(i + 2) % SP_SHARD_COLORS.length];
        return (
          <div
            key={i}
            className="jc-twinkle absolute"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.w}%`,
              height: `${s.h}%`,
              transform: `translate(-50%, -88%) rotate(${s.rot}deg)`,
              transformOrigin: '50% 100%',
              clipPath: 'polygon(50% 0%, 60% 72%, 50% 100%, 40% 72%)',
              background: `linear-gradient(180deg, #ffffff 0%, ${c1} 50%, ${c2} 100%)`,
              filter: `drop-shadow(0 0 5px ${c1})`,
              opacity: 0.95,
              animationDelay: `${(i % 5) * 0.25}s`,
            }}
          />
        );
      })}
    </div>
  );
}

/** Ornamento de esquina (volutas) reutilizable, teñido por color. */
function CornerOrnament({ color, className }: { color: string; className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <path
        d="M2 14 C2 6 6 2 14 2 M2 14 C2 9 5 6 10 6 M14 2 C9 2 6 5 6 10"
        stroke={color} strokeWidth="1.6" strokeLinecap="round"
      />
      <circle cx="6" cy="6" r="1.8" fill={color} />
    </svg>
  );
}

export type JarticardLike = Pick<Jarticard, 'type' | 'name' | 'number' | 'imageUrl' | 'crestUrl'>;

interface JarticardFrameProps {
  card: JarticardLike;
  /** Si es false, se muestra como bloqueada (silueta). Default true. */
  owned?: boolean;
  /** Nº de copias; si >1 muestra el badge de repetidas. */
  count?: number;
  className?: string;
  onClick?: () => void;
  /**
   * Pide la miniatura de 600 px en vez del original. Va en el ÁLBUM (9 cromos
   * por hoja) y en el mercado; NO en la apertura de sobres ni en el visor a
   * pantalla completa, que se ven grandes. [165]
   */
  thumb?: boolean;
}

export function JarticardFrame({ card, owned = true, count, className, onClick, thumb }: JarticardFrameProps) {
  const style = JARTICARD_STYLES[card.type] ?? JARTICARD_STYLES.titular;
  const isPrime = card.type === 'prime';
  const isSuper = card.type === 'superprime';

  return (
    <div
      onClick={onClick}
      className={cn(
        'group/jcard relative aspect-[5/7] w-full select-none [container-type:inline-size]',
        isSuper && owned ? 'overflow-visible' : 'overflow-hidden rounded-xl',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {/* Capa de marco (color / gema) */}
      <div
        className="absolute inset-0"
        style={{
          background: isSuper
            ? 'linear-gradient(135deg, #f0abfc 0%, #67e8f9 30%, #fde047 55%, #86efac 80%, #a5b4fc 100%)'
            : `linear-gradient(145deg, ${style.frame} 0%, ${style.frame2} 55%, ${style.accent} 100%)`,
          clipPath: isSuper ? SUPER_CLIP : undefined,
          borderRadius: isSuper ? undefined : '0.75rem',
          boxShadow: owned
            ? isSuper
              ? 'inset 0 0 0 2px rgba(255,255,255,0.6)'
              : `0 6px 20px -6px ${style.glow}80, inset 0 0 0 1px rgba(255,255,255,0.25)`
            : 'inset 0 0 0 1px rgba(255,255,255,0.08)',
          filter: isSuper && owned ? 'drop-shadow(0 0 22px rgba(165,243,252,0.9))' : undefined,
        }}
      />

      {/* Foil holográfico para Prime */}
      {isPrime && owned && (
        <div
          className="pointer-events-none absolute inset-0 z-30 rounded-xl opacity-40 mix-blend-overlay transition-transform duration-700 group-hover/jcard:translate-x-[40%]"
          style={{
            background:
              'linear-gradient(115deg, transparent 25%, rgba(255,0,128,0.6) 35%, rgba(0,200,255,0.6) 45%, rgba(120,255,120,0.6) 55%, transparent 65%)',
            backgroundSize: '250% 100%',
          }}
        />
      )}

      {/* Marco interior */}
      <div
        className="absolute inset-[6.5%] overflow-hidden"
        style={{
          clipPath: isSuper ? SUPER_CLIP : undefined,
          borderRadius: isSuper ? undefined : '0.5rem',
          boxShadow: `inset 0 0 0 2px rgba(255,255,255,0.35), inset 0 0 0 4px ${style.accent}`,
          background: '#0b0b0f',
        }}
      >
        {/* Imagen / silueta */}
        {owned && card.imageUrl ? (
          <Image
            thumb={thumb}
            src={card.imageUrl}
            alt={card.name}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover/jcard:scale-105"
            draggable={false}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: `radial-gradient(circle at 50% 40%, ${style.glow}22, #0b0b0f 70%)` }}
          >
            <Lock className="h-1/4 w-1/4 text-white/15" />
          </div>
        )}

        {/* Velo oscuro inferior para legibilidad del nombre */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

        {/* Escudo (opcional) */}
        {owned && card.crestUrl && (
          <div
            className="absolute right-[5%] top-[5%] z-20 aspect-square w-[22%] overflow-hidden rounded-full border bg-black/40 p-[3%] backdrop-blur-sm"
            style={{ borderColor: style.frame }}
          >
            <img src={card.crestUrl} alt="" className="h-full w-full object-contain" draggable={false} />
          </div>
        )}

        {/* Nombre */}
        <div className="absolute inset-x-0 bottom-0 z-20 px-[6%] pb-[5%] pt-2">
          <p
            className="truncate text-center font-headline font-bold leading-tight text-white drop-shadow"
            style={{ fontSize: 'clamp(0.6rem, 4.2cqw, 1.05rem)' }}
            title={card.name}
          >
            {owned ? card.name : '???'}
          </p>
        </div>

        {/* Vidrio iridiscente + destellos (Súper Prime) */}
        {isSuper && owned && (
          <>
            <div
              className="pointer-events-none absolute inset-0 z-[15] opacity-60 mix-blend-screen"
              style={{ background: 'linear-gradient(125deg, transparent 30%, rgba(255,255,255,0.5) 45%, rgba(103,232,249,0.4) 52%, rgba(240,171,252,0.4) 60%, transparent 72%)' }}
            />
            <span className="jc-holo-overlay z-[16]" />
            <div
              className="pointer-events-none absolute inset-0 z-[14] opacity-30 mix-blend-color-dodge"
              style={{ background: 'conic-gradient(from 0deg, #f0abfc, #67e8f9, #fde047, #86efac, #a5b4fc, #f0abfc)', animation: 'jc-rainbow 5s linear infinite', backgroundSize: '300% 300%' }}
            />
          </>
        )}

        {/* Ornamentos de esquina (no en la gema Súper Prime) */}
        {!isSuper && (
          <>
            <CornerOrnament color={style.frame} className="absolute left-[3%] top-[3%] z-10 w-[14%]" />
            <CornerOrnament color={style.frame} className="absolute right-[3%] top-[3%] z-10 w-[14%] -scale-x-100" />
            <CornerOrnament color={style.frame} className="absolute bottom-[3%] left-[3%] z-10 w-[14%] -scale-y-100" />
            <CornerOrnament color={style.frame} className="absolute bottom-[3%] right-[3%] z-10 w-[14%] -scale-100" />
          </>
        )}
      </div>

      {/* Picos de cristal que sobresalen (Súper Prime) */}
      {isSuper && owned && <CrystalSpikes />}

      {/* Número de carta */}
      <div
        className="absolute left-[8%] top-[8%] z-[45] flex min-w-[1.4rem] items-center justify-center rounded-md px-1 py-0.5 font-mono font-bold leading-none shadow"
        style={{ background: style.accent, color: style.frame, fontSize: 'clamp(0.5rem, 2.8cqw, 0.8rem)' }}
      >
        #{card.number}
      </div>

      {/* Etiqueta de tipo */}
      <div
        className="absolute bottom-[2.5%] left-1/2 z-[45] -translate-x-1/2 rounded-full px-2 py-[1px] font-semibold uppercase tracking-wider shadow"
        style={{
          background: isSuper
            ? 'linear-gradient(90deg,#f0abfc,#67e8f9,#fde047,#86efac,#a5b4fc)'
            : isPrime
              ? 'linear-gradient(90deg,#f59e0b,#ec4899,#8b5cf6)'
              : style.accent,
          color: isSuper ? '#06303b' : '#fff',
          fontSize: 'clamp(0.4rem, 2.4cqw, 0.65rem)',
        }}
      >
        {style.label}
      </div>

      {/* Badge de repetidas */}
      {owned && typeof count === 'number' && count > 1 && (
        <div className="absolute right-[7%] top-[7%] z-[45] flex h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full border-2 border-white/80 bg-rose-600 px-1 text-[0.7rem] font-extrabold text-white shadow-lg">
          x{count}
        </div>
      )}
    </div>
  );
}

export default JarticardFrame;
